/**
 * @fileoverview Debug utilities for development
 * Provides dev buttons for testing and debugging
 */

import { ToddlerGame } from '../core/ToddlerGame.js';
import { PremiumService } from './PremiumService.js';
import { ThemeService } from './ThemeService.js';
import { addClickAndTouch } from '../utilities/EventUtils.js';

/**
 * Debug utilities class
 */
export class DebugService {
    private static errorDisplay: HTMLElement | null = null;
    private static errorCount = 0;
    private static isShowingErrors = false;

    /**
     * Initialize debug buttons and error tracking
     */
    static init(): void {
        this.errorDisplay = document.getElementById('debugErrorDisplay');
        this.setupButtons();
        this.setupErrorTracking();
        DebugService.toggleAllDebugButtons();

        this.isShowingErrors = import.meta.env.VITE_SHOW_DEBUG_ERROR_DISPLAY === 'true';
        
        // Auto-enable error display on mobile/touch devices or when debugging
        // const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            if (this.isShowingErrors && this.errorDisplay) {
                this.errorDisplay.classList.add('visible');
                // Show diagnostic info for old iPads
                this.logError('Debug', '✅ JavaScript is running');
                this.logError('Debug', `User Agent: ${navigator.userAgent}`);
                this.logError('Debug', `Supports ES Modules: ${'noModule' in document.createElement('script')}`);
                this.logError('Debug', 'Game module loaded successfully');
            }
    }
    public static toggleAllDebugButtons(): void {
        const debugButtons = document.getElementById('debugButtons');
        if (debugButtons) {
            if (debugButtons.style.display === 'none') {
                debugButtons.style.display = '';
            } else {
                debugButtons.style.display = 'none';
            }
        }
    }

    /**
     * Setup debug button event listeners
     */
    private static setupButtons(): void {
        // Previous theme button
        const prevThemeBtn = document.getElementById('debugPrevTheme');
        if (prevThemeBtn) {
            const handler = () => this.navigateTheme('prev');
            addClickAndTouch(prevThemeBtn, handler);
        }

        // Next theme button
        const nextThemeBtn = document.getElementById('debugNextTheme');
        if (nextThemeBtn) {
            const handler = () => this.navigateTheme('next');
            addClickAndTouch(nextThemeBtn, handler);
        }

        // Reset localStorage button
        const resetBtn = document.getElementById('debugResetStorage');
        if (resetBtn) {
            const handler = () => this.resetLocalStorage();
            addClickAndTouch(resetBtn, handler);
        }

        // Toggle themes button
        const toggleThemesBtn = document.getElementById('debugToggleThemes');
        if (toggleThemesBtn) {
            const handler = () => this.toggleAllThemes();
            addClickAndTouch(toggleThemesBtn, handler);
        }

        // Show errors button
        const showErrorsBtn = document.getElementById('debugShowErrors');
        if (showErrorsBtn) {
            const handler = () => this.toggleErrorDisplay();
            addClickAndTouch(showErrorsBtn, handler);
        }
    }

    /**
     * Reset localStorage and reload page
     */
    public static resetLocalStorage(): void {
        if (localStorage.length === 0) {
            return;
        }
        if (confirm('Reset all local storage? This will clear all progress and settings.')) {
            localStorage.clear();
            console.log('🗑️ localStorage cleared');
            alert('Storage reset! Reloading page...');
            window.location.reload();
        }
    }

    /**
     * Toggle premium status to unlock/lock all themes
     */
    private static toggleAllThemes(): void {
        const currentStatus = PremiumService.checkPremiumStatus();
        const newStatus = !currentStatus.isPremiumUser;

        if (newStatus) {
            // Unlock all themes
            PremiumService.savePremiumStatus({
                isPremiumUser: true,
                unlockCode: 'DEBUG-DEV-MODE',
                unlockedAt: Date.now(),
            });
            console.log('🔓 All themes unlocked (dev mode)');
            alert('All themes unlocked! Reloading...');
        } else {
            // Lock themes
            PremiumService.savePremiumStatus({
                isPremiumUser: false,
            });
            console.log('🔒 Themes locked (free mode)');
            alert('Themes locked to free mode! Reloading...');
        }

        window.location.reload();
    }

    /**
     * Navigate to next or previous theme
     */
    private static navigateTheme(direction: 'next' | 'prev'): void {
        const game = ToddlerGame.getInstance();
        if (!game) {
            console.warn('Game instance not found');
            return;
        }

        // Get all themes (include premium themes for debugging)
        const allThemes = ThemeService.getThemeSequence(true);
        const progress = ThemeService.loadProgress();
        const currentThemeIndex = progress.currentThemeIndex;

        let newIndex: number;
        if (direction === 'next') {
            newIndex = (currentThemeIndex + 1) % allThemes.length;
        } else {
            newIndex = (currentThemeIndex - 1 + allThemes.length) % allThemes.length;
        }

        const newTheme = allThemes[newIndex];
        console.log(`🎨 Switching theme: ${game.currentTheme?.name} → ${newTheme.name}`);

        // Save new theme index to localStorage so it persists (preserve cycle count)
        ThemeService.saveProgress(newIndex, 0, progress.cycleCount || 0);

        // Update current theme and reset game
        game.currentTheme = newTheme;
        game.resetGame(true); // Clear progress for new theme
    }

    /**
     * Toggle error display visibility
     */
    private static toggleErrorDisplay(): void {
        this.isShowingErrors = !this.isShowingErrors;
        
        if (this.errorDisplay) {
            if (this.isShowingErrors) {
                this.errorDisplay.classList.add('visible');
                console.log('🐛 Error display enabled');
            } else {
                this.errorDisplay.classList.remove('visible');
                console.log('🐛 Error display disabled');
            }
        }

        const btn = document.getElementById('debugShowErrors');
        if (btn) {
            btn.textContent = this.isShowingErrors ? '🐛 Hide Errors' : '🐛 Show Errors';
        }
    }

    /**
     * Setup global error tracking
     */
    private static setupErrorTracking(): void {
        // Capture global errors
        window.addEventListener('error', (event) => {
            this.logError('Error', event.message, event.filename, event.lineno, event.colno);
        });

        // Capture unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.logError('Unhandled Promise', event.reason?.toString() || 'Unknown error');
        });

        // Override console.error to capture console errors
        const originalConsoleError = console.error;
        console.error = (...args: any[]) => {
            this.logError('Console', args.join(' '));
            originalConsoleError.apply(console, args);
        };
    }

    /**
     * Log error to display
     */
    private static logError(type: string, message: string, file?: string, line?: number, col?: number): void {
        if (!this.errorDisplay) return;

        this.errorCount++;
        
        const errorItem = document.createElement('div');
        errorItem.className = 'error-item';
        
        const timestamp = new Date().toLocaleTimeString();
        let errorText = `[${timestamp}] ${type}: ${message}`;
        
        if (file) {
            errorText += `\n  at ${file}`;
            if (line !== undefined) {
                errorText += `:${line}`;
                if (col !== undefined) {
                    errorText += `:${col}`;
                }
            }
        }
        
        errorItem.textContent = errorText;
        
        // Add to top of error display
        this.errorDisplay.insertBefore(errorItem, this.errorDisplay.firstChild);
        
        // Limit to 20 errors
        while (this.errorDisplay.children.length > 20) {
            this.errorDisplay.removeChild(this.errorDisplay.lastChild!);
        }

        // Update button badge with error count
        const btn = document.getElementById('debugShowErrors');
        if (btn && this.isShowingErrors) {
            btn.textContent = `🐛 Errors (${this.errorCount})`;
        }
    }

    /**
     * Test function to trigger a sample error
     */
    static testError(): void {
        throw new Error('Test error from DebugService.testError()');
    }
}
